<script setup lang="ts">
import { useI18n } from '#imports'
import { appName } from '~/constants'

const { setLocale, t } = useI18n()
const { getInitialLocale } = useLocalePreference()
const siteTranslation = useSiteTranslationStore()
const route = useRoute()
const isExhibition = computed(() => route.meta.exhibition === true)

useHead({
  title: appName,
})

const systemStore = useSystemStore()
let channelReady = false
let translationStarted = false
let blinkInterval: ReturnType<typeof setInterval> | undefined
let blinkTimeout: ReturnType<typeof setTimeout> | undefined

interface ChannelIO {
  (command: 'boot' | 'updateUser', options: Record<string, unknown>, callback: (error: unknown) => void): void
  (command: 'shutdown'): void
}

function getChannelIO() {
  return (window as Window & { ChannelIO?: ChannelIO }).ChannelIO
}

function syncChannelLanguage(language: string) {
  if (!channelReady || isExhibition.value)
    return
  getChannelIO()?.('updateUser', { language }, (error) => {
    if (error) {
      console.error('상담 언어 설정 실패:', error)
      systemStore.showError('상담창 언어 설정에 실패했습니다. 페이지를 새로고침해 주세요.')
    }
  })
}

watch(() => siteTranslation.currentLang, syncChannelLanguage)

function bootChannel() {
  const channelIO = getChannelIO()
  if (channelIO) {
    channelIO('boot', {
      pluginKey: 'aa1d4d89-d014-4413-91fd-a0b81a7bb96f',
      language: 'ko',
    }, (error) => {
      if (isExhibition.value) {
        channelIO('shutdown')
        return
      }
      if (error) {
        console.error('채널톡 초기화 실패:', error)
      }
      else {
        channelReady = true
        syncChannelLanguage(siteTranslation.currentLang)
        // 채널톡 초기화 성공 후 깜빡이는 텍스트 추가
        addBlinkingText()
      }
    })
  }
}

function stopExhibitionServices() {
  channelReady = false
  getChannelIO()?.('shutdown')
  clearInterval(blinkInterval)
  clearTimeout(blinkTimeout)
  document.getElementById('channel-talk-blink-text')?.remove()
  siteTranslation.stop()
}

onMounted(() => {
  watch(isExhibition, async (exhibition) => {
    if (exhibition) {
      stopExhibitionServices()
      return
    }
    // Exhibition copy is bundled locally and must never trigger live translation.
    await setLocale(getInitialLocale())
    if (isExhibition.value)
      return
    bootChannel()
    if (translationStarted) {
      await siteTranslation.selectLanguage(siteTranslation.currentLang)
    }
    else {
      translationStarted = true
      await siteTranslation.initialize()
    }
  }, { immediate: true })
})

// 깜빡이는 텍스트 추가 함수
function addBlinkingText() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  // 채널톡 버튼이 로드될 때까지 기다림
  clearInterval(blinkInterval)
  clearTimeout(blinkTimeout)
  blinkInterval = setInterval(() => {
    const channelButton = document.querySelector('#ch-plugin') || document.querySelector('.ch-plugin')
    if (channelButton) {
      clearInterval(blinkInterval)
      createBlinkingText()
    }
  }, 500)

  // 10초 후에도 찾지 못하면 그냥 실행
  blinkTimeout = setTimeout(() => {
    clearInterval(blinkInterval)
    createBlinkingText()
  }, 10000)
}

// 깜빡이는 텍스트 생성
function createBlinkingText() {
  if (isExhibition.value)
    return
  const existingText = document.getElementById('channel-talk-blink-text')
  if (existingText)
    return

  // CSS 애니메이션 추가
  if (!document.getElementById('channel-talk-blink-style')) {
    const style = document.createElement('style')
    style.id = 'channel-talk-blink-style'
    style.textContent = `
      @keyframes channelTalkBlink {
        0%, 40% { opacity: 1; }
        50%, 100% { opacity: 0; }
      }

      /* 이건 원하면 유지 / 삭제 선택 (크게 펄스 효과) */
      @keyframes channelTalkPulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.05); }
        100% { transform: scale(1); }
      }
    `
    document.head.appendChild(style)
  }

  // 텍스트 요소 생성
  const textElement = document.createElement('div')
  textElement.id = 'channel-talk-blink-text'
  textElement.textContent = t('supportContact')
  textElement.style.cssText = `
    position: fixed;
  right: 25px;
  bottom: 95px;
  background: #FAD111;
  color: black;
  padding: 10px 16px;
  border-radius: 25px;
  font-size: 14px;
  font-weight: 600;
  z-index: 999999;
  pointer-events: none;
  animation: channelTalkBlink 2s infinite steps(1, end), channelTalkPulse 4s infinite ease-in-out;
  box-shadow: 0 6px 20px rgba(250, 209, 17, 0.5);
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  border: 2px solid rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  user-select: none;
  display: block !important;
  visibility: visible !important;
  `

  // DOM에 추가
  document.body.appendChild(textElement)
}

watch(() => t('supportContact'), (label) => {
  const element = document.getElementById('channel-talk-blink-text')
  if (element)
    element.textContent = label
})

onBeforeUnmount(() => {
  clearInterval(blinkInterval)
  clearTimeout(blinkTimeout)
  siteTranslation.stop()
})
</script>

<template>
  <VitePwaManifest />

  <Loading v-if="!isExhibition" />
  <Snackbar
    v-if="!isExhibition"
    v-model="systemStore.snackbar.isVisible" :message="systemStore.snackbar.message"
    :color="systemStore.snackbar.color" :timeout="systemStore.snackbar.timeout"
    :location="systemStore.snackbar.location" :closable="systemStore.snackbar.closable"
    :icon="systemStore.snackbar.icon" :action-text="systemStore.snackbar.actionText"
    :action-color="systemStore.snackbar.actionColor" :multi-line="systemStore.snackbar.multiLine"
    :vertical="systemStore.snackbar.vertical"
  />
  <ClientOnly>
    <Suspense>
      <NuxtLayout>
        <v-app>
          <NuxtPage :key="$route.fullPath" />
        </v-app>
      </NuxtLayout>
    </Suspense>
  </ClientOnly>
</template>

<style>
  html,
body,
#__nuxt {
  height: 100vh;
  margin: 0;
  padding: 0;
  font-family: var(--font-sans);
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}

* {
  box-sizing: border-box;
}

html.dark {
  color-scheme: dark;
}

.font-pretendard {
  font-family: 'Pretendard', sans-serif !important;
}

.font-weight-semibold {
  font-weight: 600 !important;
}

.font-weight-extrabold {
  font-weight: 800 !important;
}

/* 모바일 반응형 최적화 */
@media (max-width: 768px) {
  html,
  body,
  #__nuxt,
  .v-application,
  .v-main {
    overflow-x: hidden !important;
    width: 100% !important;
    max-width: 100% !important;
  }

  .v-container {
    padding-left: 8px !important;
    padding-right: 8px !important;
    max-width: 100% !important;
  }

  .v-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .v-col {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }
}

@font-face {
  font-family: 'Pretendard';
  src: url('https://fastly.jsdelivr.net/gh/Project-Noonnu/noonfonts_2107@1.1/Pretendard-Regular.woff') format('woff');
}
</style>
